/**
 * Landing-page copy and figures.
 *
 * Kept as data rather than inline JSX so the Figma redesign can rebuild the
 * component layer without retyping (and mistyping) the numbers.
 *
 * Every figure here is carried over from the CareBee product page on
 * aximedic.com and the investor deck. Do not adjust one without adjusting its
 * source — these are cited market and pricing claims, not decoration.
 */

export const PILLARS = [
  {
    title: 'Care Captain™',
    body:
      'One family member is designated as Care Captain and holds real authority over a dependent’s care — appointments, plan changes, clinical notifications, and coordination between providers. One Captain can hold that role for more than one person, which matters when you are caring for both parents.',
    note: 'Authority is cryptographically enforced, so a provider can verify who is allowed to act before they act.',
  },
  {
    title: 'Circle of Care',
    body:
      'Everyone involved — siblings, physicians, specialists, therapists, home aides — gets exactly the level of access their role calls for. Care-plan updates, medication changes and appointment moves reach the right people without a group text.',
    note: 'Synchronised through AXERP Health over HL7-FHIR R4, so clinical systems and the household stay in agreement.',
  },
  {
    title: 'Medical Power of Attorney — included',
    body:
      'Every subscription includes Medical Power of Attorney. Standalone legal services charge around $199 for a will-and-POA package; here it comes with the app, because a Care Captain without legal standing is not much use in an emergency room.',
    note: 'Medical Advisor and second-opinion access add on at $50 or $100 a month. Patient advocacy is quoted per case.',
  },
  {
    title: 'Ambient AI Scribe',
    body:
      'Appointments are recorded and turned into structured clinical notes automatically, using AWS HealthScribe. What the specialist said gets captured accurately instead of half-remembered in the car afterwards.',
    note: 'Output is coded FHIR R4 — usable by clinicians, not just a transcript.',
  },
] as const;

/**
 * Caregiving-burden figures. Sourced from the same research the product page
 * and investor deck cite (AARP/NAC caregiving prevalence, employer
 * productivity-loss estimates).
 */
export const STATS = [
  { value: '63M', label: 'Americans caring for an adult family member — up 45% in a decade' },
  { value: '27 hrs', label: 'Average unpaid caregiving week — 24% give 40 hours or more' },
  { value: '26%', label: 'Report difficulty coordinating care, up from 19%' },
  { value: '$522B', label: 'Annual US employer productivity loss' },
] as const;

export const PRICING = [
  {
    name: 'Household',
    price: '$20',
    unit: '/mo',
    body:
      'The whole household, not per person. Care Captain, Circle of Care, records, medications, appointments, and Medical Power of Attorney.',
    points: ['Medical POA included', 'Multiple dependents per Captain'],
    featured: true,
  },
  {
    name: 'Medical Advisor',
    price: '$50',
    unit: '–$100/mo',
    body:
      'Add a credentialed medical advisor and second-opinion access when a diagnosis needs a second set of eyes. Basic or expanded.',
    points: ['400+ credentialed experts', 'Cancel or add any month'],
    featured: false,
  },
  {
    name: 'Advocacy',
    price: 'On quote',
    unit: '',
    body:
      'Hands-on patient advocacy for the hard cases — denied claims, disputed care plans, complex transitions. Priced per case.',
    points: ['Referral pharmacy network', 'High-touch, local service'],
    featured: false,
  },
] as const;

export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Request access',
    body:
      'Tell us who you are caring for. Early access is being released in waves alongside our first discharge-planning partners, so we review each request.',
  },
  {
    step: '02',
    title: 'We confirm your place',
    body:
      'You hear back from a person, normally within one business day, with your onboarding link and what to expect from the release you are joining.',
  },
  {
    step: '03',
    title: 'Create your household',
    body:
      'Name a Care Captain, add the people in your circle, and get the diagnosis, medications, appointments and paperwork into one record.',
  },
] as const;

export const TRUST_MARKS = [
  'HIPAA-eligible infrastructure',
  'Blockchain-anchored audit trails',
  'HL7-FHIR R4 native',
  'Native iOS & Android',
] as const;
